import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import MDBox from '../components/MDBox';
import MDTypography from '../components/MDTypography';
import PaidIcon from '@mui/icons-material/Paid';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { db, collection, getDocs, query, where, getDoc, doc } from '../firebaseConfig';
import { usePeriod } from '../components/PeriodContext';

// Extend Date prototype for week calculation
Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const TeamWinLossStats = ({ userID, userIDs, tradeAccountID }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [journalEntries, setJournalEntries] = useState({});
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const targetUserIDs = userID ? [userID] : userIDs || [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        let allTrades = [];
        let allCapitalTransactions = [];

        for (const id of targetUserIDs) {
          // Check user permission
          const userDoc = await getDoc(doc(db, 'Users', id));
          if (!userDoc.exists() || !userDoc.data().profileInfo?.teamPermissions?.[userDoc.data().profileInfo?.teamId]) {
            console.log(`TeamWinLossStats: Skipping user ${id} due to missing permissions`);
            continue;
          }

          // Fetch trades
          const tradesQuery = query(
            collection(db, `Users/${id}/tradeAccounts/${userID ? tradeAccountID : 'default'}/trades`),
            where('status', '==', showMissedTrades ? 'Missed' : 'Closed')
          );
          const tradesSnapshot = await getDocs(tradesQuery);
          const userTrades = tradesSnapshot.docs.map((doc) => ({
            id: doc.id,
            date: doc.data().date instanceof Object ? doc.data().date.toDate() : new Date(doc.data().date),
            returns: Number(doc.data().returns) || 0,
            tags: doc.data().tags || [],
            status: doc.data().status,
          }));
          allTrades = [...allTrades, ...userTrades];

          // Fetch capital
          const capitalRef = collection(db, `Users/${id}/capital`);
          const capitalSnapshot = await getDocs(capitalRef);
          const userCapital = capitalSnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            date: doc.data().date ? new Date(doc.data().date) : new Date(),
          }));

          allCapitalTransactions = [...allCapitalTransactions, ...userCapital];
        }


        processData(allTrades, allCapitalTransactions);
      } catch (error) {
        setJournalEntries({});
      }
    };

    const calculateInitialCapital = (capitalTransactions) => {
      let total = 0;
      capitalTransactions.forEach((trans) => {
        if (trans.deposit) total += Number(trans.deposit);
        if (trans.deposits) total += Number(trans.deposits);
        if (trans.withdrawal) total -= Number(trans.withdrawal);
        if (trans.withdrawals) total -= Number(trans.withdrawals);
      });
      return total;
    };

    const processData = (trades, capitalTransactions) => {
      let totalWins = 0;
      let totalLosses = 0;
      let winCount = 0;
      let lossCount = 0;
      let bestTradeWindow = 'N/A';
      let streakDots = [];
      let dailyReturns = {};
      let maxDrawdown = 0;
      let maxDrawdownAmount = 0;
      let relativeMaxDrawdown = 0;
      let relativeMaxDrawdownAmount = 0;
      let mostProfitableDay = 0;
      const periodStart = getPeriodStartDate();
      const actualStartingCapital = calculateInitialCapital(capitalTransactions);

      const filteredTrades = trades
        .filter((entry) => entry.status === (showMissedTrades ? 'Missed' : 'Closed'))
        .filter((entry) => {
          const entryDate = new Date(entry.date);
          const matchesTag =
            selectedTag === 'all' || (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
          const periodMatch = matchesPeriod(entryDate);
          return periodMatch && matchesTag;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      filteredTrades.forEach((entry) => {
        const returns = parseFloat(entry.returns);
        if (isNaN(returns)) {
          console.warn(`TeamWinLossStats: Invalid returns for trade ${entry.id}: ${entry.returns}`);
          return;
        }
        const entryDate = new Date(entry.date);
        const dateKey = entryDate.toDateString();
        if (!dailyReturns[dateKey]) dailyReturns[dateKey] = 0;
        dailyReturns[dateKey] += returns;
        if (returns > 0) {
          totalWins += returns;
          winCount++;
        } else if (returns < 0) {
          totalLosses += Math.abs(returns);
          lossCount++;
        }
      });

      let peak = actualStartingCapital;
      let runningTotal = 0;
      let currentCapital = actualStartingCapital;
      Object.keys(dailyReturns)
        .sort((a, b) => new Date(a) - new Date(b))
        .forEach((date) => {
          const dailyReturn = dailyReturns[date];
          runningTotal += dailyReturn;
          currentCapital = actualStartingCapital + runningTotal;
          if (dailyReturn > mostProfitableDay) mostProfitableDay = dailyReturn;
          if (currentCapital > peak && currentCapital > 0) peak = currentCapital;
          if (currentCapital > 0 && peak > 0) {
            const drawdownAmount = peak - currentCapital;
            const drawdownPercent = Math.min((drawdownAmount / peak) * 100, 100);
            if (drawdownAmount > 0) {
              if (drawdownPercent > maxDrawdown) {
                maxDrawdown = drawdownPercent;
                maxDrawdownAmount = drawdownAmount;
              }
              const relativeDrawdownPercent = Math.min((drawdownAmount / currentCapital) * 100, 100);
              if (relativeDrawdownPercent > relativeMaxDrawdown) {
                relativeMaxDrawdown = relativeDrawdownPercent;
                relativeMaxDrawdownAmount = drawdownAmount;
              }
            }
          }
        });

      Object.values(dailyReturns).forEach((dailyReturn) => {
        streakDots.push(dailyReturn > 0 ? 'green' : dailyReturn < 0 ? 'red' : 'neutral');
      });

      const sessions = calculateSessionPerformance(filteredTrades);
      bestTradeWindow = determineBestSession(sessions);
      const currentStreakCount = calculateCurrentStreak(dailyReturns);
      const averageWin = winCount > 0 ? (totalWins / winCount).toFixed(2) : 0;
      const averageLoss = lossCount > 0 ? (totalLosses / lossCount).toFixed(2) : 0;
      const winLossRatio = lossCount > 0 ? (winCount / lossCount).toFixed(2) : '∞';
      const consistencyPercent = totalWins > 0 ? (100 - (mostProfitableDay / totalWins) * 100).toFixed(2) : 0;

      setJournalEntries({
        totalWins,
        totalLosses,
        winCount,
        lossCount,
        averageWin,
        averageLoss,
        winLossRatio,
        bestTradeWindow,
        currentStreak: streakDots.slice(-10),
        streakDots,
        currentStreakCount,
        maxDrawdown: maxDrawdown.toFixed(2),
        maxDrawdownAmount,
        relativeMaxDrawdown: relativeMaxDrawdown.toFixed(2),
        relativeMaxDrawdownAmount,
        peak: peak.toFixed(2),
        currentCapital: currentCapital.toFixed(2),
        consistencyPercent: Math.max(consistencyPercent, 0),
      });
    };

    if (targetUserIDs.length > 0) fetchData();
    else setJournalEntries({});
  }, [targetUserIDs, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag]);

  const getPeriodStartDate = () => {


    const now = new Date();
    switch (selectedPeriod) {
      case 'today': return new Date(now.setHours(0, 0, 0, 0));
      case 'this-week': return new Date(now.setDate(now.getDate() - now.getDay()));
      case 'this-month': return new Date(now.getFullYear(), now.getMonth(), 1);
      case 'this-year': return new Date(now.getFullYear(), 0, 1);
      case 'pick-month': return new Date(selectedYear, selectedMonth - 1, 1);
      case 'pick-year': return new Date(selectedYear, 0, 1);
      case 'pick-month-year': return new Date(selectedYear, selectedMonth - 1, 1);
      case 'all-time': return new Date(1970, 0, 1);
      default: return new Date(1970, 0, 1);
    }

  };

  const matchesPeriod = (date) => {
    const periodStart = getPeriodStartDate();
    if (selectedPeriod === 'this-week') {
      return date.getWeek() === new Date().getWeek() && date.getFullYear() === new Date().getFullYear();
    }
    return date >= periodStart;
  };

  const calculateSessionPerformance = (entries) => {
    const sessions = { 'New York': { wins: 0, total: 0 }, London: { wins: 0, total: 0 }, Asia: { wins: 0, total: 0 } };
    entries.forEach((entry) => {
      const matchesTag = selectedTag === 'all' || (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
      if (matchesPeriod(new Date(entry.date)) && matchesTag) {
        const session = categorizeSession(entry.date, entry.entryTime);
        sessions[session].total++;
        if (parseFloat(entry.returns) > 0) sessions[session].wins++;
      }
    });
    return sessions;
  };

  const categorizeSession = (date, time) => {
    if (!time) return 'Asia'; // Fallback if time is missing
    const hour = new Date(`${date} ${time}`).getUTCHours();
    if (hour >= 22 || hour < 7) return 'Asia';
    if (hour >= 7 && hour < 16) return 'London';
    if (hour >= 12 && hour < 21) return 'New York';
    return 'Asia';
  };

  const determineBestSession = (sessions) => {
    const minTrades = 2;
    let bestSession = 'N/A';
    let bestWinRate = 0;
    Object.entries(sessions).forEach(([session, stats]) => {
      if (stats.total >= minTrades) {
        const winRate = stats.wins / stats.total;
        if (winRate > bestWinRate) {
          bestWinRate = winRate;
          bestSession = session;
        }
      }
    });
    return bestSession;
  };

  const calculateCurrentStreak = (dailyReturns) => {
    const dates = Object.keys(dailyReturns).sort((a, b) => new Date(b) - new Date(a));
    let streak = 0;
    for (const date of dates) {
      if (dailyReturns[date] > 0) streak++;
      else break;
    }
    return streak;
  };

  const {
    totalWins = 0,
    totalLosses = 0,
    winCount = 0,
    lossCount = 0,
    averageWin = 0,
    averageLoss = 0,
    peak = 0,
    winLossRatio = '∞',
    maxDrawdown = 0,
    maxDrawdownAmount = 0,
    relativeMaxDrawdown = 0,
    relativeMaxDrawdownAmount = 0,
    bestTradeWindow = 'N/A',
    currentStreak = [],
    currentStreakCount = 0,
    consistencyPercent = 0,
  } = journalEntries;

  const renderStreakDots = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ marginRight: '8px' }}>
          {currentStreak.map((dot, index) => (
            <span
              key={index}
              style={{ fontSize: '.9rem', color: dot === 'green' ? 'green' : dot === 'red' ? 'red' : 'grey' }}
            >
              ●
            </span>
          ))}
        </div>
        <MDTypography variant="button" fontWeight="normal" color="text">
          {currentStreakCount} Day Streak
        </MDTypography>
      </div>
    );
  };

  const tooltipContent = `The Trading Performance Metrics track key stats for ${
    showMissedTrades ? 'missed' : 'closed'
  } trades ${targetUserIDs.length > 1 ? 'across the team' : 'for the user'}: Win/Loss Ratio, Average Win/Loss, Max Drawdown, Best Trade Window, Streak, Consistency %.`;

  return (
    <Card style={{ minHeight: '260px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <PaidIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text">
              Win/Loss Stats {showMissedTrades ? '(Missed)' : ''} {targetUserIDs.length > 1 ? '(Team)' : ''}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
            <MDTypography variant="h4" style={{ visibility: 'hidden' }}>
              0
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox pb={2} px={2}>
          <MDTypography component="p" variant="button" color="text" display="flex">
            Average Win: <MDTypography component="span" variant="button" fontWeight="bold" color="success">
              ${averageWin}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex">
            Average Loss: <MDTypography component="span" variant="button" fontWeight="bold" color="error">
              ${averageLoss}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex">
            Win/Loss Ratio: <MDTypography component="span" variant="button" fontWeight="bold" color={winLossRatio > 1 ? 'success' : 'error'}>
              {winLossRatio}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex">
            Max Drawdown: <MDTypography component="span" variant="button" fontWeight="bold" color="error">
              {maxDrawdown}% (${maxDrawdownAmount.toFixed(2)})
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex">
            Consistency %: <MDTypography component="span" variant="button" fontWeight="bold" color={consistencyPercent >= 70 ? 'success' : consistencyPercent >= 30 ? 'warning' : 'error'}>
              {consistencyPercent}%
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex">
            Best Trade Window: <MDTypography component="span" variant="button" fontWeight="bold" color="info">
              {bestTradeWindow}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex">
            Streak: {renderStreakDots()}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
};

TeamWinLossStats.propTypes = {
  userID: PropTypes.string,
  userIDs: PropTypes.arrayOf(PropTypes.string),
  tradeAccountID: PropTypes.string,
};

TeamWinLossStats.defaultProps = {
  userID: null,
  userIDs: [],
  tradeAccountID: 'default',
};

export default TeamWinLossStats;